import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { useParams } from 'react-router-dom';

// store
import { useStoreValue, storeSet } from '../../../../store';

// helpers
import { timecodeToSeconds } from '../../../../helpers/timecodeUtils';

// components
import { TimelineEditor } from '../../../../components/TimelineEditor';

const CORE_LAYER = 'CORE';
const GENERATE_LAYER = 'GENERATE_NEW';
//const LAYER_HEIGHT_BASE = 65;
const LAYER_GAP_UNIT = 8;


const buildTimelineTracks = (session, cachedRecAudioUrls) => {
    const tracks = [];

    session.coreLayer.tracks.forEach(track => {
        tracks.push({
            ...track,
            layer: CORE_LAYER,
            start: timecodeToSeconds(track.timecode),
            duration: (track.instanceDuration?.parsedValue ?? 0) / 1000,
            actualDuration: (track.clip?.duration ?? 0) / 1000,
            startPlayback: (track.startPlayback?.parsedValue ?? 0) / 1000,
            audioUrl: cachedRecAudioUrls[track.clip?.id] ?? track.clip?.url,
            draggable: true,
            filename: track.clip?.name,
        });
    });

    session.auxLayers.forEach((auxLayer, i) => {
        const layerName = `AUX_${i}`;
        auxLayer.tracks.forEach(track => {
            tracks.push({
                ...track,
                layer: layerName,
                start: timecodeToSeconds(track.timecode),
                duration: (track.instanceDuration?.parsedValue ?? 0) / 1000,
                actualDuration: (track.clip?.duration ?? 0) / 1000,
                startPlayback: (track.startPlayback?.parsedValue ?? 0) / 1000,
                audioUrl: cachedRecAudioUrls[track.clip?.id] ?? track.clip?.url,
                draggable: true,
                filename: track.clip?.name,
            });
        });
    });

    return tracks;
};

const RecSessionTimeline = () => {
    const { projectId } = useParams();
    const recSessions = useStoreValue('recSessions');
    const selectedRecItemId = useStoreValue('selectedRecItemId');
    const cachedRecAudioUrls = useStoreValue('cachedRecAudioUrls');
    const timelineEditorUiCollapsed = useStoreValue('timelineEditorUiCollapsed');
    const timelineState = useStoreValue('timelineState');
    const timelineVolume = useStoreValue('timelineVolume');
    const timelineEditingMode = useStoreValue('timelineEditingMode');

    const selectedSession = useMemo(() =>
        recSessions.find(s => s.id === selectedRecItemId) ?? null,
    [recSessions, selectedRecItemId]);

    const timelineTracks = useMemo(() => {
        if (!selectedSession) return [];
        return buildTimelineTracks(selectedSession, cachedRecAudioUrls);
    }, [selectedSession, cachedRecAudioUrls]);

    useEffect(() => {
        storeSet('cleanupRecSessionAudio');
        if (selectedSession) {
            storeSet('cacheRecSessionAudio', projectId, selectedSession);
        }
    }, [selectedRecItemId]); // eslint-disable-line react-hooks/exhaustive-deps

    const layers = useMemo(() => {
        const base = selectedSession
            ? [CORE_LAYER, ...selectedSession.auxLayers.map((_, i) => `AUX_${i}`)]
            : [CORE_LAYER];
        return timelineEditingMode === 'generateInstrument' ? [...base, GENERATE_LAYER] : base;
    }, [selectedSession, timelineEditingMode]);

    const layerHeightMap = useMemo(() => ({
        [CORE_LAYER]: 160
    }), [timelineEditorUiCollapsed]);

    const layerGapMap = useMemo(() => ({
        [CORE_LAYER]: LAYER_GAP_UNIT * 3
    }), []);

    const isPlaying = timelineState === 'playing';
    const [controlledTime, setControlledTime] = useState(null);
    const timelineRef = useRef({ duration: 0 });

    const handlePlaybackChange = useCallback((playing) => {
        storeSet('timelineState', playing ? 'playing' : 'paused');
    }, []);

    const handleTimeChange = useCallback((time) => {
        timelineRef.current.time = time;
    }, []);

    const handleDurationChange = useCallback((duration) => {
        timelineRef.current.duration = duration;
    }, []);

    useEffect(() => {
        if (timelineState === 'stopped') {
            setControlledTime(0);
            storeSet('timelineState', 'paused');
        } else if (timelineState === 'backward') {
            setControlledTime(0);
            storeSet('timelineState', isPlaying ? 'playing' : 'paused');
        } else if (timelineState === 'forward') {
            setControlledTime(timelineRef.current.duration || 999999);
            storeSet('timelineState', isPlaying ? 'playing' : 'paused');
        }
    }, [timelineState, isPlaying]);

    useEffect(() => {
        if (controlledTime !== null) {
            const timer = setTimeout(() => setControlledTime(null), 100);
            return () => clearTimeout(timer);
        }
    }, [controlledTime]);

    const handleLayerSelection = useCallback(({ layer, startPos, endPos }) => {
        storeSet('stemSplitterSelection', { layer, startPos, endPos, timelineType: 'rec' });
    }, []);

    const handleGenerateLayerSelection = useCallback(({ layer, startPos, endPos }) => {
        storeSet('generateInstrumentSelection', { layer, startPos, endPos, timelineType: 'rec' });
    }, []);

    const getTrackVolume = useCallback((track) => {
        if (timelineVolume === 0) return 0;
        const masterVol = timelineVolume / 100;
        if (track.layer === 'CORE') return masterVol;
        const match = track.layer.match(/^AUX_(\d+)$/);
        if (match) {
            const idx = parseInt(match[1], 10);
            const auxLayer = selectedSession?.auxLayers[idx];
            if (!auxLayer || auxLayer.volume === 0) return 0;
            return masterVol * (auxLayer.volume / 100);
        }
        return masterVol;
    }, [timelineVolume, selectedSession]);

    return (
        <>
            <span className="recSessionTitle">
                {__('Recording Session', 'soundbetween')}
            </span>
            {!selectedSession
                ? <div className="recSessionTimeline__empty">
                    <span className="recSessionTimeline__emptyText">
                        {__('Select a recording session', 'soundbetween')}
                    </span>
                  </div>
                : <TimelineEditor
                    key={selectedSession.id}
                    mode="rec"
                    tracks={timelineTracks}
                    layers={layers}
                    layerHeightMap={layerHeightMap}
                    layerGapMap={layerGapMap}
                    hideVideo
                    isPlayingExternal={isPlaying}
                    currentTimeExternal={controlledTime}
                    timelineEditingMode={timelineEditingMode}
                    getTrackVolume={getTrackVolume}
                    onPlaybackChange={handlePlaybackChange}
                    onTimeChange={handleTimeChange}
                    onDurationChange={handleDurationChange}
                    onLayerSelectionComplete={handleLayerSelection}
                    generateInstrumentLayer={timelineEditingMode === 'generateInstrument' ? GENERATE_LAYER : null}
                    onGenerateLayerSelectionComplete={handleGenerateLayerSelection}
                  />
            }
        </>
    );
};

export default RecSessionTimeline;
